import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SerialScanButton } from "@/components/SerialScanner";

const SerialPicker = ({ code, serials, onChange }) => {
  const [value, setValue] = useState("");
  const add = (s) => {
    const v = (s || value).trim();
    if (!v) return;
    if (serials.includes(v)) toast.info(`Seriale ${v} già contato`);
    else onChange([...serials, v]);
    setValue("");
  };
  return (
    <div className="mt-2">
      <div className="flex gap-2">
        <Input className="h-10 font-mono" placeholder="Seriale" value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid={`count-serial-input-${code}`} />
        <Button type="button" variant="outline" className="h-10" onClick={() => add()} data-testid={`count-serial-add-${code}`}>Aggiungi</Button>
        <SerialScanButton onDetected={add} testId={`count-serial-scan-${code}`} />
      </div>
      {serials.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {serials.map((s) => (
            <span key={s} className="inline-flex items-center gap-1 border bg-muted/50 px-2 py-0.5 font-mono text-xs" data-testid={`count-serial-chip-${s}`}>
              {s}<button type="button" onClick={() => onChange(serials.filter((x) => x !== s))} aria-label={`Rimuovi ${s}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const CountForm = ({ count, onDone, onReload }) => {
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [pick, setPick] = useState("");
  useEffect(() => { api.get("/warehouse/items").then((r) => setCatalog(r.data.filter((i) => i.active))).catch(() => {}); }, []);
  useEffect(() => {
    setRows(Object.fromEntries(count.lines.map((l) => [l.item_id, { qty: l.counted_qty ?? "", serials: l.counted_serials || [], touched: l.counted_qty != null }])));
  }, [count]);
  const set = (id, patch) => setRows((r) => ({ ...r, [id]: { ...r[id], ...patch, touched: true } }));
  const payload = () => ({ lines: count.lines.map((l) => {
    const r = rows[l.item_id] || {};
    if (l.serialized) return { item_id: l.item_id, counted_qty: r.touched ? r.serials.length : null, counted_serials: r.serials || [] };
    return { item_id: l.item_id, counted_qty: r.qty === "" || r.qty == null ? null : Number(r.qty), counted_serials: [] };
  }) });
  const save = async (submit) => {
    setBusy(true);
    try {
      await api.put(`/warehouse/counts/${count.id}/lines`, payload());
      if (submit) await api.post(`/warehouse/counts/${count.id}/submit`);
      toast.success(submit ? "Conta consegnata per l'approvazione" : "Conta salvata");
      onDone?.(submit);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const addItem = async () => {
    const it = catalog.find((i) => `${i.code} · ${i.name}` === pick || i.code.toLowerCase() === pick.trim().toLowerCase());
    if (!it) return toast.error("Seleziona un articolo dall'elenco");
    try {
      await api.put(`/warehouse/counts/${count.id}/lines`, payload());
      await api.post(`/warehouse/counts/${count.id}/add-item`, { item_id: it.id });
      setPick("");
      onReload?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`count-form-${count.number}`}>
      <p className="mb-3 text-xs text-muted-foreground">Conta cieca: inserisci quello che trovi fisicamente. Lascia vuoto un articolo se non lo hai contato; per gli articoli a seriale aggiungi o scansiona ogni seriale presente (o segna "nessuno").</p>
      <div className="divide-y border bg-white">
        {count.lines.map((l) => (
          <div key={l.item_id} className="p-3" data-testid={`count-line-${l.code}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm"><span className="font-mono text-xs font-semibold">{l.code}</span> {l.name}</div>
              {!l.serialized ? (
                <div className="flex items-center gap-2">
                  <Input className="h-10 w-28" type="number" min="0" step="any" value={rows[l.item_id]?.qty ?? ""} onChange={(e) => set(l.item_id, { qty: e.target.value })} data-testid={`count-qty-${l.code}`} />
                  <span className="w-8 text-xs text-muted-foreground">{l.unit}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono font-semibold" data-testid={`count-serial-total-${l.code}`}>{rows[l.item_id]?.touched ? `${rows[l.item_id].serials.length} pz` : "non contato"}</span>
                  {!rows[l.item_id]?.touched ? <button type="button" className="underline" onClick={() => set(l.item_id, { serials: [] })} data-testid={`count-serial-none-${l.code}`}>nessuno</button>
                    : <button type="button" className="text-muted-foreground underline" onClick={() => setRows((r) => ({ ...r, [l.item_id]: { ...r[l.item_id], serials: [], touched: false } }))} data-testid={`count-serial-reset-${l.code}`}>azzera</button>}
                </div>
              )}
            </div>
            {l.serialized && <SerialPicker code={l.code} serials={rows[l.item_id]?.serials || []} onChange={(serials) => set(l.item_id, { serials })} />}
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Input list={`count-catalog-${count.id}`} className="h-10 bg-white" placeholder="Hai trovato un articolo non in elenco? Cerca codice o descrizione" value={pick} onChange={(e) => setPick(e.target.value)} data-testid="count-add-item-input" />
        <datalist id={`count-catalog-${count.id}`}>{catalog.filter((i) => !count.lines.some((l) => l.item_id === i.id)).map((i) => <option key={i.id} value={`${i.code} · ${i.name}`} />)}</datalist>
        <Button type="button" variant="outline" className="h-10" onClick={addItem} data-testid="count-add-item-button">Aggiungi</Button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="count-save-button">Salva conta</Button>
        <Button onClick={() => save(true)} disabled={busy} data-testid="count-submit-button">Consegna per approvazione</Button>
      </div>
    </div>
  );
};
